import React, {useEffect, useMemo, useRef, useState} from "react";
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import Button from "@mui/material/Button";
import {
    ContentCopyOutlined,
    EditOutlined,
    FileCopyOutlined,
    PlayArrowOutlined,
    StopOutlined,
    UploadFileOutlined
} from "@mui/icons-material";
import {useSnackbar} from "../../providers/SnackbarProvider";
import {
    deleteProgram,
    fetchProgramContent,
    fetchProgramTests,
    setDefaultProgram,
    uploadProgram
} from "./dlvApi";
import {countResults, useDlvTestRun} from "./useDlvTestRun";
import {DlvRunSummary} from "./DlvTestResults";
import {AspCodeView} from "./AspCodeView";
import {DlvCodeEditor} from "./DlvYamlEditor";
import {highlightAsp} from "./yamlHighlight";
import {mergeTests, readFormCases} from "./formModel";
import {copyText} from "./copyText";
import {MONOSPACE_FONT} from "./dlvStyles";

const PROGRAM_NAME_PATTERN = /^[A-Za-z0-9_.-]{1,100}\.dl$/;
const BUTTON_SX = {textTransform: 'none'};

function suggestName(fileName) {
    const clean = fileName.replace(/[^A-Za-z0-9_.-]/g, '_');
    return PROGRAM_NAME_PATTERN.test(clean) ? clean : clean.replace(/\.[^.]*$/, '') + '.dl';
}

// "programma-v4.dl" -> "programma-v4-copia.dl", then "-copia-2"...
function suggestCopyName(sourceName, existing) {
    const stem = sourceName.replace(/\.dl$/, '');
    let candidate = `${stem}-copia.dl`;
    for (let i = 2; existing.includes(candidate); i++) {
        candidate = `${stem}-copia-${i}.dl`;
    }
    return PROGRAM_NAME_PATTERN.test(candidate) ? candidate : '';
}

function ProgressLabel({busy, children}) {
    return <>{busy && <CircularProgress size={16} color="inherit" sx={{mr: 1}}/>}{children}</>;
}

// Creates a new program with the text of an existing one
function DuplicateProgramDialog({open, sourceName, content, programs, onClose, onDuplicated}) {
    const [name, setName] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open) {
            setName(suggestCopyName(sourceName, programs));
            setBusy(false);
            setError(null);
        }
        // The suggestion is made when the dialog opens: the list can change while it is open
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const exists = programs.includes(name);
    const nameError = name !== '' && !PROGRAM_NAME_PATTERN.test(name)
        ? 'Usa solo lettere, numeri, trattino, trattino basso e punto, e termina con .dl'
        : (exists ? 'Esiste già un programma con questo nome' : null);
    const canSubmit = !busy && PROGRAM_NAME_PATTERN.test(name) && !exists;

    const handleSubmit = async () => {
        if (!canSubmit) {
            return;
        }
        setBusy(true);
        setError(null);
        try {
            onDuplicated(await uploadProgram({name, content, overwrite: false}), name);
        } catch (e) {
            setError(e.message);
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
            <DialogTitle>Duplica programma</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{pt: 1}}>
                    <Typography>
                        Crea un nuovo programma con lo stesso contenuto di <b>{sourceName}</b>. I test salvati non
                        vengono copiati: puoi importarli dopo.
                    </Typography>
                    <TextField
                        autoFocus
                        label="Nome del nuovo programma"
                        value={name}
                        onChange={(e) => setName(e.target.value.trim())}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleSubmit();
                            }
                        }}
                        error={Boolean(nameError)}
                        helperText={nameError ?? 'Il programma viene salvato nella cartella stroke'}
                        disabled={busy}
                        inputProps={{autoComplete: 'off', spellCheck: false}}
                        fullWidth/>
                    {error && <Alert severity="error">{error}</Alert>}
                </Stack>
            </DialogContent>
            <DialogActions sx={{px: 3, py: 2}}>
                <Button onClick={onClose} disabled={busy} sx={BUTTON_SX}>Annulla</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit} sx={BUTTON_SX}>
                    <ProgressLabel busy={busy}>Duplica</ProgressLabel>
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export function ViewProgramDialog({open, programName, isDefault, programs, onClose, onSaved, onDuplicated}) {
    const {createSnackbar} = useSnackbar();
    const [state, setState] = useState({status: 'loading', content: '', error: null});
    const [attempt, setAttempt] = useState(0);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [duplicating, setDuplicating] = useState(false);

    useEffect(() => {
        if (!open) {
            return undefined;
        }
        let mounted = true;
        setState({status: 'loading', content: '', error: null});
        setEditing(false);
        setSaving(false);
        setSaveError(null);
        setDuplicating(false);
        fetchProgramContent(programName)
            .then(response => mounted && setState({status: 'ready', content: response.content, error: null}))
            .catch(e => mounted && setState({status: 'error', content: '', error: e.message}));
        return () => {
            mounted = false;
        };
    }, [open, programName, attempt]);

    const changed = editing && draft !== state.content;
    const canSave = changed && draft.trim() !== '' && !saving;

    // Closing or leaving the editor would lose the changes: ask first
    const confirmDiscard = () => !changed || window.confirm('Hai modifiche non salvate al programma. Vuoi scartarle?');

    const handleClose = () => {
        if (!saving && confirmDiscard()) {
            onClose();
        }
    };

    const handleStartEditing = () => {
        setDraft(state.content);
        setSaveError(null);
        setEditing(true);
    };

    const handleCancelEditing = () => {
        if (confirmDiscard()) {
            setEditing(false);
            setSaveError(null);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setSaveError(null);
        try {
            const response = await uploadProgram({name: programName, content: draft, overwrite: true});
            setState(s => ({...s, content: draft}));
            setEditing(false);
            setSaving(false);
            onSaved(response);
            createSnackbar({message: 'Programma salvato', type: 'success'});
        } catch (e) {
            setSaveError(e.message);
            setSaving(false);
        }
    };

    const handleCopy = async () => {
        const copied = await copyText(editing ? draft : state.content);
        createSnackbar(copied
            ? {message: 'Programma copiato', type: 'success'}
            : {message: 'Non è stato possibile copiare il programma', type: 'error'});
    };

    const shown = editing ? draft : state.content;
    const lineCount = state.status === 'ready' ? shown.replace(/\n$/, '').split('\n').length : 0;

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg" scroll="paper">
            <DialogTitle>
                <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap flexWrap="wrap">
                    <span>{programName}</span>
                    {isDefault && <Chip size="small" color="primary" variant="outlined" label="Predefinito"/>}
                    {editing && <Chip size="small" color="warning" variant="outlined" label="In modifica"/>}
                    {lineCount > 0 &&
                        <Typography variant="body2" color="text.secondary" component="span">
                            {lineCount} {lineCount === 1 ? 'riga' : 'righe'}
                        </Typography>}
                </Stack>
            </DialogTitle>
            <DialogContent dividers sx={{p: 0}}>
                {state.status === 'loading' &&
                    <Stack alignItems="center" sx={{py: 8}}><CircularProgress aria-label="Caricamento"/></Stack>}
                {state.status === 'error' &&
                    <Box sx={{p: 3}}>
                        <Alert severity="error"
                               action={<Button color="inherit" size="small" onClick={() => setAttempt(a => a + 1)}
                                               sx={BUTTON_SX}>Riprova</Button>}>
                            {state.error}
                        </Alert>
                    </Box>}
                {state.status === 'ready' && isDefault &&
                    <Box sx={{p: 2, pb: 0}}>
                        <Alert severity="info">
                            Il programma predefinito non si può modificare: modifica una copia e impostala come
                            predefinita.
                        </Alert>
                    </Box>}
                {state.status === 'ready' && !editing && <AspCodeView text={state.content}/>}
                {state.status === 'ready' && editing &&
                    <Box sx={{p: 2}}>
                        <DlvCodeEditor
                            id="dlv-program-editor"
                            label={`Contenuto di ${programName}`}
                            value={draft}
                            onChange={setDraft}
                            highlight={highlightAsp}
                            disabled={saving}
                            minHeight={420}
                            maxHeight="60vh"/>
                        <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
                            Salvando sostituisci il programma sul servizio DLV. Tab indenta; premi Esc e poi Tab per
                            uscire dall'editor.
                        </Typography>
                        {draft.trim() === '' &&
                            <Alert severity="warning" sx={{mt: 2}}>Il programma non può essere vuoto.</Alert>}
                        {saveError && <Alert severity="error" sx={{mt: 2}}>{saveError}</Alert>}
                    </Box>}
            </DialogContent>
            <DialogActions sx={{px: 3, py: 2}}>
                <Button startIcon={<ContentCopyOutlined/>} onClick={handleCopy} disabled={state.status !== 'ready'}
                        sx={BUTTON_SX}>
                    Copia
                </Button>
                <Button startIcon={<FileCopyOutlined/>} onClick={() => setDuplicating(true)}
                        disabled={state.status !== 'ready' || editing} sx={BUTTON_SX}>
                    Duplica
                </Button>
                <Box sx={{flexGrow: 1}}/>
                {!editing && state.status === 'ready' && !isDefault &&
                    <Button startIcon={<EditOutlined/>} onClick={handleStartEditing} sx={BUTTON_SX}>
                        Modifica
                    </Button>}
                {editing
                    ? <>
                        <Button onClick={handleCancelEditing} disabled={saving} sx={BUTTON_SX}>
                            Annulla modifiche
                        </Button>
                        <Button variant="contained" onClick={handleSave} disabled={!canSave} sx={BUTTON_SX}>
                            <ProgressLabel busy={saving}>Salva programma</ProgressLabel>
                        </Button>
                    </>
                    : <Button variant="contained" onClick={handleClose} sx={BUTTON_SX}>Chiudi</Button>}
            </DialogActions>
            <DuplicateProgramDialog
                open={duplicating}
                sourceName={programName}
                content={state.content}
                programs={programs}
                onClose={() => setDuplicating(false)}
                onDuplicated={onDuplicated}/>
        </Dialog>
    );
}

export function UploadProgramDialog({open, onClose, programs, defaultProgram, onUploaded}) {
    const [name, setName] = useState('');
    const [content, setContent] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const fileInput = useRef(null);

    useEffect(() => {
        if (open) {
            setName('');
            setContent('');
            setBusy(false);
            setError(null);
        }
    }, [open]);

    const exists = programs.includes(name);
    const isDefault = name === defaultProgram;
    const nameError = name !== '' && !PROGRAM_NAME_PATTERN.test(name)
        ? 'Usa solo lettere, numeri, trattino, trattino basso e punto, e termina con .dl'
        : (isDefault ? 'Il programma predefinito non si può sovrascrivere: per cambiarlo, impostane un altro come predefinito' : null);
    const canSubmit = !busy && PROGRAM_NAME_PATTERN.test(name) && !isDefault && content.trim() !== '';

    const handleFile = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) {
            return;
        }
        setContent(await file.text());
        if (name === '') {
            setName(suggestName(file.name));
        }
    };

    const handleSubmit = async () => {
        setBusy(true);
        setError(null);
        try {
            const response = await uploadProgram({name, content, overwrite: exists});
            onUploaded(response, name);
        } catch (e) {
            setError(e.message);
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="md" scroll="paper">
            <DialogTitle>Carica programma</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2.5} sx={{pt: 1}}>
                    <Stack direction={{xs: 'column', sm: 'row'}} spacing={1.5} alignItems={{sm: 'flex-start'}}>
                        <TextField
                            label="Nome del file"
                            placeholder="programma-v5.dl"
                            value={name}
                            onChange={(e) => setName(e.target.value.trim())}
                            error={Boolean(nameError)}
                            helperText={nameError ?? 'Il programma viene salvato nella cartella stroke'}
                            fullWidth/>
                        <input ref={fileInput} type="file" accept=".dl,.lp,.asp,.txt,text/plain" hidden
                               onChange={handleFile}/>
                        <Button
                            variant="outlined"
                            startIcon={<UploadFileOutlined/>}
                            onClick={() => fileInput.current?.click()}
                            sx={{...BUTTON_SX, flexShrink: 0, height: 56}}>
                            Scegli un file
                        </Button>
                    </Stack>
                    <TextField
                        label="Contenuto del programma"
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        multiline
                        minRows={10}
                        maxRows={18}
                        fullWidth
                        inputProps={{spellCheck: false, style: {fontFamily: MONOSPACE_FONT, fontSize: '0.8rem'}}}/>
                    {exists && !isDefault &&
                        <Alert severity="warning">
                            Esiste già un programma con questo nome: salvando lo sostituisci.
                        </Alert>}
                    {error && <Alert severity="error">{error}</Alert>}
                </Stack>
            </DialogContent>
            <DialogActions sx={{px: 3, py: 2}}>
                <Button onClick={onClose} disabled={busy} sx={BUTTON_SX}>Annulla</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit} sx={BUTTON_SX}>
                    <ProgressLabel busy={busy}>{exists ? 'Sostituisci programma' : 'Carica programma'}</ProgressLabel>
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// The code to type to confirm: the name of the program as letters and digits, like the one asked to delete a form
export function deleteConfirmationCode(programName) {
    const code = programName.replace(/\.dl$/, '').replace(/[^0-9a-z]/gi, '').slice(0, 16).toLowerCase();
    return code || 'pleasedeleteit';
}

export function DeleteProgramDialog({open, programName, hasTests, onClose, onDeleted}) {
    const [confirmation, setConfirmation] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open) {
            setConfirmation('');
            setBusy(false);
            setError(null);
        }
    }, [open]);

    const code = deleteConfirmationCode(programName);
    const deleteEnabled = !busy && confirmation.trim().toLowerCase() === code;

    const handleDelete = async () => {
        if (!deleteEnabled) {
            return;
        }
        setBusy(true);
        setError(null);
        try {
            onDeleted(await deleteProgram(programName));
        } catch (e) {
            setError(e.message);
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
            <DialogTitle>Elimina programma</DialogTitle>
            <DialogContent>
                <Stack spacing={2}>
                    <Alert severity="error">
                        Stai per eliminare definitivamente <b>{programName}</b>
                        {hasTests ? ' e i suoi test salvati' : ''}. L'operazione non si può annullare. Per confermare
                        scrivi <b>{code}</b>.
                    </Alert>
                    <TextField
                        autoFocus
                        id="dlv-delete-confirmation"
                        label="Codice di conferma"
                        value={confirmation}
                        onChange={(e) => setConfirmation(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleDelete();
                            }
                        }}
                        disabled={busy}
                        variant="standard"
                        inputProps={{autoComplete: 'off', spellCheck: false}}
                        fullWidth/>
                    {error && <Alert severity="error">{error}</Alert>}
                </Stack>
            </DialogContent>
            <DialogActions sx={{px: 3, py: 2}}>
                <Button onClick={onClose} disabled={busy} sx={BUTTON_SX}>Annulla</Button>
                <Button variant="contained" color="error" onClick={handleDelete} disabled={!deleteEnabled}
                        sx={BUTTON_SX}>
                    <ProgressLabel busy={busy}>Elimina</ProgressLabel>
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export function SetDefaultProgramDialog({open, programName, hasTests, defaultProgram, cases, casesError, onClose, onChanged}) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const {state, start, stop, reset} = useDlvTestRun();

    useEffect(() => {
        if (open) {
            setBusy(false);
            setError(null);
            reset();
        }
    }, [open, reset]);

    const {passed, failed} = useMemo(() => countResults(state.results), [state.results]);
    const running = state.status === 'running';
    const testsFinished = state.status === 'finished';
    const failedCases = useMemo(() => state.cases
        .map((testCase, index) => ({index, description: testCase.description, result: state.results[index]}))
        .filter(item => item.result && !item.result.passed), [state.cases, state.results]);

    const handleConfirm = async () => {
        setBusy(true);
        setError(null);
        try {
            onChanged(await setDefaultProgram(programName));
        } catch (e) {
            setError(e.message);
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
            <DialogTitle>Imposta come predefinito</DialogTitle>
            <DialogContent>
                <Stack spacing={2.5}>
                    <Typography>
                        I triage useranno subito <b>{programName}</b> per generare i risultati. Il programma
                        predefinito attuale resta salvato come {defaultProgram.replace(/\.dl$/, '.prev.dl')}.
                        {hasTests && ` I test salvati di ${programName} sostituiscono quelli del predefinito.`}
                    </Typography>

                    <Box>
                        <Typography variant="subtitle2" sx={{mb: 1}}>Prima di procedere</Typography>
                        {state.status === 'idle' &&
                            <Typography variant="body2" color="text.secondary" sx={{mb: 1.5}}>
                                {cases
                                    ? `Puoi eseguire su questo programma i ${cases.length} test dell'editor.`
                                    : (casesError ?? "Aggiungi dei test nell'editor per poterli eseguire qui.")}
                            </Typography>}
                        {state.status !== 'idle' && <DlvRunSummary state={state}/>}
                        <Stack direction="row" spacing={1} sx={{mt: 1.5}}>
                            {!running &&
                                <Button
                                    variant="outlined"
                                    startIcon={<PlayArrowOutlined/>}
                                    disabled={!cases || busy}
                                    onClick={() => start(cases, programName)}
                                    sx={BUTTON_SX}>
                                    {state.status === 'idle' ? 'Esegui i test' : 'Esegui di nuovo'}
                                </Button>}
                            {running &&
                                <Button variant="outlined" startIcon={<StopOutlined/>} onClick={stop} sx={BUTTON_SX}>
                                    Interrompi
                                </Button>}
                        </Stack>
                    </Box>

                    {testsFinished && failed === 0 &&
                        <Alert severity="success">Tutti i test sono passati.</Alert>}
                    {testsFinished && failed > 0 &&
                        <Alert severity="warning">
                            {failed} {failed === 1 ? 'test non è passato' : 'test non sono passati'} su {passed + failed}.
                            Se confermi, i triage useranno comunque questo programma.
                            <Box component="ul" sx={{m: 0, mt: 1, pl: 2.5}}>
                                {failedCases.slice(0, 5).map(item =>
                                    <li key={item.index}>
                                        {item.description?.trim() ? item.description : `Caso ${item.index + 1}`}
                                    </li>)}
                                {failedCases.length > 5 && <li>e altri {failedCases.length - 5}</li>}
                            </Box>
                        </Alert>}
                    {state.status === 'failed' && <Alert severity="error">{state.error.message}</Alert>}
                    {state.status === 'stopped' &&
                        <Alert severity="info">Test interrotti: puoi rieseguirli o procedere senza.</Alert>}
                    {error && <Alert severity="error">{error}</Alert>}
                </Stack>
            </DialogContent>
            <DialogActions sx={{px: 3, py: 2}}>
                <Button onClick={onClose} disabled={busy} sx={BUTTON_SX}>Annulla</Button>
                <Button
                    variant="contained"
                    color={failed > 0 ? 'warning' : 'primary'}
                    onClick={handleConfirm}
                    disabled={busy || running}
                    sx={BUTTON_SX}>
                    <ProgressLabel busy={busy}>Imposta come predefinito</ProgressLabel>
                </Button>
            </DialogActions>
        </Dialog>
    );
}

/**
 * Copies the tests saved for another program into the editor (as a draft: they are saved with "Salva test").
 */
export function ImportTestsDialog({open, programs, currentName, currentInput, onClose, onImport}) {
    const [source, setSource] = useState('');
    const [loaded, setLoaded] = useState({status: 'idle', content: '', error: null});
    const [attempt, setAttempt] = useState(0);
    const [mode, setMode] = useState('append');

    const candidates = useMemo(
        () => programs.filter(program => program.hasTests && program.name !== currentName), [programs, currentName]);

    useEffect(() => {
        if (open) {
            setSource('');
            setLoaded({status: 'idle', content: '', error: null});
            setMode('append');
        }
    }, [open]);

    useEffect(() => {
        if (!open || !source) {
            return undefined;
        }
        let mounted = true;
        setLoaded({status: 'loading', content: '', error: null});
        fetchProgramTests(source)
            .then(response => mounted && setLoaded({status: 'ready', content: response.content, error: null}))
            .catch(e => mounted && setLoaded({status: 'error', content: '', error: e.message}));
        return () => {
            mounted = false;
        };
    }, [open, source, attempt]);

    const ready = loaded.status === 'ready';
    const imported = useMemo(() => ready ? readFormCases(loaded.content) : null, [ready, loaded.content]);
    const hasCurrent = currentInput.trim() !== '';
    const merge = useMemo(
        () => ready && hasCurrent ? mergeTests(currentInput, loaded.content) : null,
        [ready, hasCurrent, currentInput, loaded.content]);
    const effectiveMode = hasCurrent && !merge?.error ? mode : 'replace';

    const handleImport = () => {
        onImport(effectiveMode === 'append' ? merge.text : loaded.content, source);
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Importa test</DialogTitle>
            <DialogContent>
                <Stack spacing={2.5} sx={{pt: 1}}>
                    {candidates.length === 0
                        ? <Alert severity="info">Nessun altro programma ha dei test salvati.</Alert>
                        : <>
                            <Typography>
                                Copia nell'editor i test salvati per un altro programma. Per conservarli su{' '}
                                <b>{currentName}</b> poi premi Salva test.
                            </Typography>
                            <TextField
                                select
                                label="Programma"
                                value={source}
                                onChange={(e) => {
                                    setSource(e.target.value);
                                    setMode('append');
                                }}
                                fullWidth>
                                {candidates.map(program =>
                                    <MenuItem key={program.name} value={program.name}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <span>{program.name}</span>
                                            {program.isDefault &&
                                                <Chip size="small" color="primary" variant="outlined" label="Predefinito"/>}
                                        </Stack>
                                    </MenuItem>)}
                            </TextField>
                        </>}

                    {loaded.status === 'loading' && <Stack alignItems="center"><CircularProgress size={24}/></Stack>}
                    {loaded.status === 'error' &&
                        <Alert severity="error"
                               action={<Button color="inherit" size="small" onClick={() => setAttempt(a => a + 1)}
                                               sx={BUTTON_SX}>Riprova</Button>}>
                            {loaded.error}
                        </Alert>}
                    {ready && loaded.content.trim() === '' &&
                        <Alert severity="warning">Questo programma non ha più test salvati.</Alert>}
                    {ready && loaded.content.trim() !== '' &&
                        <Typography color="text.secondary">
                            {imported.error
                                ? `I test di ${source} non sono leggibili dal modulo, ma si possono importare lo stesso.`
                                : `${imported.cases.length} test da importare.`}
                        </Typography>}

                    {ready && loaded.content.trim() !== '' && hasCurrent &&
                        <FormControl>
                            <FormLabel id="dlv-import-mode">Nell'editor ci sono già dei test</FormLabel>
                            <RadioGroup aria-labelledby="dlv-import-mode" value={effectiveMode}
                                        onChange={(e) => setMode(e.target.value)}>
                                <FormControlLabel value="append" control={<Radio/>} disabled={Boolean(merge?.error)}
                                                  label="Aggiungi in coda ai test attuali"/>
                                <FormControlLabel value="replace" control={<Radio/>}
                                                  label="Sostituisci i test attuali"/>
                            </RadioGroup>
                            {merge?.error && <FormHelperText>{merge.error}</FormHelperText>}
                        </FormControl>}
                </Stack>
            </DialogContent>
            <DialogActions sx={{px: 3, py: 2}}>
                <Button onClick={onClose} sx={BUTTON_SX}>Annulla</Button>
                <Button variant="contained" onClick={handleImport} disabled={!ready || loaded.content.trim() === ''}
                        sx={BUTTON_SX}>
                    Importa test
                </Button>
            </DialogActions>
        </Dialog>
    );
}
